import { getLabel, request } from '@weapp/utils';
import { observable, action, computed, toJS } from 'mobx';
import { UploadData, UploadProps, Dialog, DialogConfirmProps, MDialog, DialogMessageProps } from '@weapp/ui';
import { ReactNode } from "react";
import { IFile } from '@weapp/ui/lib/components/upload/types';
import { CustStepType, StepChangeType } from './types';
import { i18n } from './i18n';

const { confirm, message } = Dialog;
const { toast } = MDialog;
const isEmpty = (val: any) => val == null || (!['number', 'string'].includes(typeof (val)) && !(Object.keys(val) || val).length);

const showConfirm = (props: DialogConfirmProps) =>
    confirm({
        ...props,
    });
const showMessage = (props: DialogMessageProps) => message(props);
const onConfirmHandle = (props: DialogConfirmProps) =>
    showConfirm({
        okText: i18n.ok(),
        okType: "primary",
        cancelText: i18n.cancel(),
        cancelType: "default",
        title: i18n.confirm(),
        ...props,
    });

function isMobile() {
    return window.location.pathname.indexOf('/mobile') === 0 || /(iPhone|iPad|iPod|iOS|Android)/i.test(navigator.userAgent);
}

const onErrorHandle = (content?: ReactNode) => {
    const func: any = isMobile() ? toast : showMessage;
    func({ type: isMobile() ? "fail" : "error", content });
};

export class ImportDataComStore {
    @observable loading: boolean = false;
    @observable visible: boolean = false;

    private timer: any = null;
    private rateKey: string = '';
    module: string = '';
    importUrl: string = '';
    importUrlParams: any = {};
    uploadUrlParams: any = {};
    saveUrlParams: any = {};

    @observable activeStep = 0;
    @computed get firstStep() {
        return (this.activeStep === 0);
    }
    @computed get lastStep() {
        return (this.steps.length === (this.activeStep + 1));
    }

    getState = (index: number) => {
        return this.activeStep === index ? (this.dataError ? 'error' : 'process') : (this.activeStep > index ? 'finish' : 'wait');
    }

    get defaultSteps(): any[] {
        return [
            { key: 'upload', title: i18n['61532']() },
            { key: 'preview', title: i18n['61531']() },
            { key: 'importData', title: i18n['61534']() }
        ]
    }

    @observable _steps: any[] = [];
    @computed get steps(): any[] {
        let arr = toJS(this._steps) || [];
        if (isEmpty(arr))
            arr.push(...this.defaultSteps);
        return arr.map((data: any, index: number) => {
            return { ...data, status: this.getState(index) }
        })
    }
    set steps(v) { this._steps = v }

    @observable importReqData: any = {};
    @observable uploadApi: string = '';
    @observable dataSaveApi: string = '';
    @observable errorApi: string = '';
    @observable rateApi: string = '';
    @observable removeCacheApi: string = '';
    @observable tipInfos: any = {};
    @observable uploadedData: any[] = [];
    @computed get uploadProps(): UploadProps {
        return {
            uploadUrl: this.uploadApi,
            maxCount: 1,
            maxSize: 2,
            limitType: 'xls,xlsx',
            dragUpload: true,
            data: this.uploadedData
            // showListUploaded:false
        }
    }

    @observable dataError: boolean = false;

    @action setState = (params: any, callback?: () => void) => {
        Object.keys(params).forEach((key) => {
            if (key in this) {
                // @ts-ignore
                this[key] = params[key];
            }
        });

        callback && callback()
    }

    @action clear = () => {
        this.dispose();
        this.setState({ importReqData: {}, activeStep: 0, processing: false, processingData: {}, uploadedData: [] })
    }

    @action init = (params: any) => {
        this.clear();
        this.setState({ ...params, activeStep: 0 }, this.getImportForm);
    }

    @action getImportForm = async () => {
        this.setState({ loading: true });
        const response = await request({ url: this.importUrl, method: 'get', params: this.importUrlParams });
        if (response.status) {
            // const {dataSaveApi, errorApi, rateApi, tipInfos, uploadApi} = response.data;
            this.setState({ ...response.data, importReqData: response.data });
        } else {
            onErrorHandle(response.msg);
        }
        this.setState({ loading: false });
    }

    @observable _previewData: any = {};//ITableProps = {columns: [], data: []};
    @computed get previewData() { return toJS(this._previewData) }
    set previewData(v) { this._previewData = v }

    @action onUploadChange = (data?: UploadData[], callback?: Function) => {
        // const previewData = data?.[0]?.table;
        // if(!isEmpty(previewData)){
        //     this.setState({previewData});
        //     this.onStepChangeHandle(1);
        // }
        this.setState({ previewData: data?.[0], uploadedData: data });
        // this.onStepChangeHandle(1);
        // const stepData = this.steps[this.activeStep];
        // callback?.(this.activeStep, stepData);
    }

    onError = (file: IFile, errorInfo: string, response?: any) => {
        onErrorHandle(errorInfo);
        // console.debug(file, errorInfo, response)
    }

    @action onStepChangeHandle = async (step: number, doValidator?: CustStepType, callback?: StepChangeType) => {
        let valid: any = false;
        if (doValidator && step > 0) {
            valid = await doValidator(this.activeStep, this.steps[this.activeStep].key, this.previewData);
        } else {
            valid = true;
        }

        if (!valid) return;

        const nextStep = this.activeStep + step;
        const stepData = this.steps[nextStep];
        const { key } = stepData;

        const onChange = () => {
            this.setState({ activeStep: nextStep });
            callback?.(nextStep, stepData);
        }

        if (key === 'preview' && isEmpty(this.previewData)) {
            onErrorHandle(i18n['61535']());
            return;
        } if (key === 'upload') {
            onConfirmHandle({
                content: getLabel('216942', '是否放弃已上传的文件?'),
                onOk: () => {
                    this.setState({ previewData: {}, uploadedData: [] });
                    onChange();
                },
                onCancel: () => {
                    return;
                }
            });
        } else if (key === 'importData') {
            this.doSaveData();
            onChange();
        } else {
            onChange();
        }
    }

    @observable processing: boolean = false;
    @action doSaveData = async () => {
        this.rateKey = '' + new Date().getTime();
        this.setState({ processing: true });
        setTimeout(() => {
            this.processingMonitor();
        }, 0);
        try {
            const response = await request({ url: this.dataSaveApi, method: 'post', data: { rateKey: this.rateKey, ...this.saveUrlParams }, timeout: 600000 });
            if (response.status) {
                // this.dispose();
                this.setState({ processingData: response.data?.importRate });
            } else {
                this.dispose();
                onErrorHandle(response.msg);
            }
        } catch (err: any) {
            this.dispose();
        }
        // this.setState({ processing: false });
    }

    @observable processingData: any = {};
    @action processingMonitor = async () => {
        this.timer != null && clearInterval(this.timer);
        if (!this.rateApi) return;
        this.timer = setInterval(async () => {
            const response = await request({ url: this.rateApi, method: 'get', params: { rateKey: this.rateKey } });
            if (response.status) {
                const total = response.data?.importRate?.total;
                const rate = response.data?.importRate?.rate;
                const failure = response.data?.importRate?.failure;
                const success = response.data?.importRate?.success;
                // 不是导入过程的错误，检测excel数据的错误时直接停止
                const excel_self_error = response.data?.excel_self_error;
                if (rate === 100 || ((total === failure + success) && total != 0) || excel_self_error) {
                    this.dispose();
                }
                this.setState({ processingData: response.data?.importRate });

            } else {
                this.dispose();
                message({
                    type: 'info',
                    content: response.msg
                })
            }
        }, 1000);
    }

    @action onDone = async (callback?: () => void) => {
        // if(this.processing) return;
        if (!this.removeCacheApi) {
            this.clear();
            callback?.();
        } else {
            const response = await request({ url: this.removeCacheApi, method: 'post', data: { rateKey: this.rateKey } });
            if (response.status) {
                this.clear();
                callback?.();
            } else {
                onErrorHandle(response.msg);
            }
        }
    }

    @action dispose = () => {
        this.timer != null && clearInterval(this.timer);
        this.setState({ processing: false });
    }
}

const instance = new ImportDataComStore();
export default instance;